/** Etappe 1 der Bestandsübernahme: nachsehen, was in der Ablage liegt. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'

export type Endungsbefund = {
  endung: string
  anzahl: number
  bytes: number
  beispiele: string[]
}

export type Inventurergebnis = {
  ordner: number
  dateien: number
  bytes: number
  max_tiefe: number
  tiefste_pfade: string[]
  endungen: Endungsbefund[]
  pdf: {
    gesamt: number
    geprueft: number
    mit_text: number
    scan: number
    unlesbar: number
    stichprobe: number
  }
}

export type Inventurlauf = {
  id: string
  ordner: string
  status: 'laeuft' | 'fertig' | 'gescheitert'
  begonnen_am: string | null
  beendet_am: string | null
  dateien: number
  ordner_anzahl: number
  bytes_gesamt: number
  ergebnis: Inventurergebnis | null
  fehler: string | null
  benutzer_name: string | null
}

const schluessel = ['inventur'] as const

export function useInventurlaeufe() {
  return useQuery({
    queryKey: schluessel,
    queryFn: ({ signal }) => api.holen<Inventurlauf[]>('/api/inventur', signal),
  })
}

/** Ein laufender Lauf wird abgefragt, ein fertiger nicht mehr. */
export function useInventurlauf(id: string | null) {
  return useQuery({
    queryKey: [...schluessel, id] as const,
    enabled: id !== null,
    queryFn: ({ signal }) => api.holen<Inventurlauf>(`/api/inventur/${id}`, signal),
    refetchInterval: (abfrage) =>
      abfrage.state.data?.status === 'laeuft' ? 2000 : false,
  })
}

export function useInventurStarten() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (ordner: string) =>
      api.senden<Inventurlauf>('/api/inventur', { ordner }),
    onSuccess: () => speicher.invalidateQueries({ queryKey: schluessel }),
  })
}

// ── Etappe 2: Vorschläge und Übernahme ───────────────────────────

export type Vorschlag = {
  id: string
  art: 'kunde' | 'spielplatz'
  name: string
  kunde_name: string | null
  plz: string | null
  ort: string | null
  quellen: string[] | null
  dateien: number
  sicher: boolean
  hinweis: string | null
  stand: 'offen' | 'uebernommen' | 'verworfen'
}

export type Kundenblock = {
  kunde: Vorschlag
  plaetze: Vorschlag[]
  dateien: number
  zu_pruefen: number
}

export type Vorschlagsliste = {
  lauf_id: string
  ordner: string
  /** Stand des Einlesens: offen, laeuft, fertig, gescheitert. */
  status: 'offen' | 'laeuft' | 'fertig' | 'gescheitert'
  fehler: string | null
  bloecke: Kundenblock[]
  kunden: number
  plaetze: number
  zu_pruefen: number
  offen: number
}

export function useVorschlaege(laufId: string | null) {
  return useQuery({
    queryKey: [...schluessel, laufId, 'vorschlaege'] as const,
    enabled: laufId !== null,
    queryFn: ({ signal }) =>
      api.holen<Vorschlagsliste>(`/api/inventur/${laufId}/vorschlaege`, signal),
    // Während gelesen wird, nachfragen – sonst bliebe „Liest …" stehen.
    refetchInterval: (abfrage) =>
      abfrage.state.data?.status === 'laeuft' ? 2000 : false,
  })
}

export function useVorschlaegeSammeln(laufId: string | null) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (ordner?: string) =>
      api.senden<Vorschlagsliste>(`/api/inventur/${laufId}/vorschlaege`, {
        ordner: ordner ?? '',
      }),
    onSuccess: (liste) =>
      speicher.setQueryData([...schluessel, laufId, 'vorschlaege'], liste),
  })
}

export type UebernahmeErgebnis = {
  kunden_neu: number
  kunden_bekannt: number
  plaetze_neu: number
  plaetze_bekannt: number
}

export function useVorschlaegeUebernehmen(laufId: string | null) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (posten: string[]) =>
      api.senden<UebernahmeErgebnis>(`/api/inventur/${laufId}/uebernehmen`, {
        posten,
      }),
    onSuccess: () => {
      void speicher.invalidateQueries({ queryKey: [...schluessel, laufId] })
      void speicher.invalidateQueries({ queryKey: ['kunden'] })
    },
  })
}

export function useVorschlaegeVerwerfen(laufId: string | null) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (posten: string[]) =>
      api.senden<Vorschlagsliste>(`/api/inventur/${laufId}/verwerfen`, { posten }),
    onSuccess: (liste) =>
      speicher.setQueryData([...schluessel, laufId, 'vorschlaege'], liste),
  })
}
